import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import { postWithUserTableParams } from "../services/fetchWrapper";
import { Environment } from "../utils/Environment";
import SuccessMessageComponent from "./SuccessMessageComponent";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
// import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const apiUrl = Environment.getApiUrl();

//matching per names of dropdowns in upload comparison
const REQUIRED_TABLES = [
    "flowlines",
    "structures",
    "wetlands",
    "developed",
    "concrete",
    "steel",
    "allwater",
    "roadways",
    "railways"
];

//Creating key value pair for scoring, important key doesn't change
const SCORE_CALCULATIONS = [
    { key: "flowline", label: "Flowlines" },
    { key: "structure", label: "Structures" },
    { key: "developed", label: "Land Developed" },
    { key: "railway", label: "Railways" },
    { key: "water", label: "Waterbodies" },
    { key: "wetland", label: "Wetlands" },
    { key: "concrete", label: "Concrete Corrosion" },
    { key: "steel", label: "Steel Corrosion" },
    { key: "road", label: "Roadways" },
];

const ConfirmTablesForScoreProcessing: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [userTables, setUserTables] = useState<string[]>([]);
    const [tableMapping, setTableMapping] = useState<Record<string, string>>({});
    const [showSuccess, setShowSuccess] = useState(false);
    const [processingDone, setProcessingDone] = useState(false);

    useEffect(() => {
        const fetchTables = async () => {
            setLoading(true);
            try {
                // 1. Get customer data
                const username = localStorage.getItem("userName");
                if (!username) {
                    alert("No username found.");
                    setLoading(false);
                    return;
                }
                const client_id = localStorage.getItem("client_id") || "";
                const job_id = localStorage.getItem("job_id") || "";
                const usernameToSend = username; // from localStorage above

                const tablesResponse = await postWithUserTableParams(
                    `${apiUrl}/get-user-tables`,
                    { job_id, client_id, username: usernameToSend }
                );
                const tablesData = await tablesResponse.json();
                const filteredTables = (tablesData.tables || []).filter((table: string) => {
                    // Get base name before last "_"
                    const baseName = table.includes("_") ? table.substring(0, table.lastIndexOf("_")) : table;
                    return REQUIRED_TABLES.includes(baseName);
                });
                setUserTables(filteredTables);
            } catch (error) {
                alert("Failed to fetch tables. Please try again.");
            } finally {
                setLoading(false);
            }
        };
        fetchTables();
    }, []);

    const handleTableChange = (scoreKey: string, tableName: string) => {
        setTableMapping((prev) => ({
            ...prev,
            [scoreKey]: tableName,
        }));
    };

    const handleSave = async () => {
        const client_id = localStorage.getItem("client_id") || "";
        const job_id = localStorage.getItem("job_id") || "";

        // Build the array of table objects
        const toSave = Object.entries(tableMapping)
            .filter(([_, table_name]) => table_name)
            .map(([score_type, table_name]) => ({
                client_id,
                job_id,
                table_name,
                score_type,
            }));

        try {
            const response = await fetch(`${apiUrl}/save-customer-calc-tables`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ tables: toSave, client_id }),
            });
            if (!response.ok) {
                throw new Error("Failed to save table mappings");
            }
            setShowSuccess(true);
            localStorage.setItem("tablesSaved", "true");
        } catch (error) {
            // swallow — X shown via processingDone
        } finally {
            setProcessingDone(true);
        }
    };

    if (loading) return <CircularProgress />;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
                mt: 2,
                mb: 1,
            }}
        >
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, textTransform: "uppercase" }}>
                Confirm and Match Tables for Score Processing
            </Typography>
            <svg width="292" height="6" viewBox="0 0 292 6" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: 16 }}>
                <path d="M0 3H292" stroke="#8AD019" strokeWidth="6" />
            </svg>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    // background: "#e3f2fd",
                    borderRadius: 1,
                    p: 1,
                    mb: 2,
                    gap: 1,
                    maxWidth: 520,
                    width: "100%",
                }}
            >
                {/* <CloudUploadIcon color="primary" sx={{ mt: "2px" }} /> */}
                <Typography sx={{ fontSize: 16 }}>
                    The most recently uploaded tables are always used for score processing.
                    If you need to update a table, please go back and upload a new version.
                </Typography>
            </Box>
            {SCORE_CALCULATIONS.map((calc) => (
                <Box
                    key={calc.key}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                        justifyContent: "center",
                        width: "100%",
                        maxWidth: 560,
                    }}
                >
                    <Typography sx={{ minWidth: 150, fontWeight: 500, mr: 1.5 }}>
                        {calc.label}
                    </Typography>
                    <TextField
                        select
                        size="small"
                        label="Select Table"
                        value={tableMapping[calc.key] || ""}
                        onChange={(e) => handleTableChange(calc.key, e.target.value)}
                        sx={{
                            minWidth: 220,
                            flex: 1,
                            width: 300,
                            maxWidth: "100%",
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: "#F6FFE8",
                            }
                        }}
                    >
                        {userTables.map((table) => (
                            <MenuItem key={table} value={table}>
                                {table}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
            ))}
            <Box
                sx={{
                    position: "sticky",
                    bottom: 0,
                    backgroundColor: "background.paper",
                    py: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                }}
            >
                <Button
                    variant="contained"
                    color="primary"
                    disabled={Object.values(tableMapping).filter(Boolean).length === 0}
                    onClick={handleSave}
                    sx={{ backgroundColor: "#0373B5" }}
                >
                    Save Table Mapping
                </Button>
                {showSuccess ? (
                    <SuccessMessageComponent message="Mapping complete!" compact />
                ) : (
                    processingDone && (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <HighlightOffIcon sx={{ fontSize: 40, color: "#828282" }} />
                            <Typography variant="body2" sx={{ color: "#828282" }}>
                                Scoring did not complete successfully.
                            </Typography>
                        </Box>
                    )
                )}
            </Box>
        </Box>
    );
};

export default ConfirmTablesForScoreProcessing;